import React, { useState } from 'react';
import { colors, typography, spacing, radii } from '../styles/shared';
import { Upload, X } from 'lucide-react';

const styles = {
  container: {
    marginBottom: spacing[6],
  },
  title: {
    ...typography.h4,
    color: colors.gray[900],
    marginBottom: spacing[4],
  },
  optionsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: spacing[4],
    maxWidth: '400px',
  },
  colorOption: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing[3],
  },
  label: {
    ...typography.body2,
    color: colors.gray[700],
    fontWeight: '500',
    flex: 1,
  },
  colorInput: {
    width: '60px',
    height: '32px',
    padding: '2px',
    border: `1px solid ${colors.gray[300]}`,
    borderRadius: radii.md,
    cursor: 'pointer',
    '&:hover': {
      borderColor: colors.gray[400],
    },
  },
  logoUpload: {
    marginTop: spacing[4],
    padding: spacing[4],
    border: `2px dashed ${colors.gray[300]}`,
    borderRadius: radii.lg,
    cursor: 'pointer',
    transition: 'all 150ms ease',
    '&:hover': {
      borderColor: colors.primary[400],
      backgroundColor: colors.primary[50],
    },
  },
  logoUploadContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: spacing[2],
  },
  uploadIcon: {
    color: colors.gray[400],
  },
  uploadText: {
    ...typography.body2,
    color: colors.gray[600],
    textAlign: 'center',
  },
  logoPreviewContainer: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: spacing[6],
  },
  logoPreview: {
    position: 'relative',
    width: '100px',
    height: '100px',
  },
  logoImage: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    borderRadius: radii.md,
    border: `1px solid ${colors.gray[200]}`,
  },
  logoControls: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: spacing[3],
  },
  logoSizeControl: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing[2],
  },
  rangeInput: {
    width: '100%',
  },
  removeButton: {
    position: 'absolute',
    top: -8,
    right: -8,
    backgroundColor: colors.gray[100],
    border: `1px solid ${colors.gray[300]}`,
    borderRadius: '50%',
    width: '24px',
    height: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: colors.gray[200],
    },
  },
};

const QRCodeCustomizer = ({ 
  fgColor, 
  bgColor, 
  onFgColorChange, 
  onBgColorChange,
  onLogoChange,
  onLogoSizeChange,
}) => {
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoSize, setLogoSize] = useState(30); // Default 30% of QR code size

  const handleLogoUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target.result);
        onLogoChange(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveLogo = () => {
    setLogoPreview(null);
    onLogoChange(null);
  };

  const handleLogoSizeChange = (e) => {
    const newSize = parseInt(e.target.value);
    setLogoSize(newSize);
    onLogoSizeChange(newSize);
  };

  return (
    <div style={styles.container}>
      <h4 style={styles.title}>Customize QR Code</h4>
      <div style={styles.optionsGrid}>
        <div style={styles.colorOption}>
          <label style={styles.label}>QR Code Color</label>
          <input
            type="color"
            value={fgColor}
            onChange={(e) => onFgColorChange(e.target.value)}
            style={styles.colorInput}
          />
        </div>
        <div style={styles.colorOption}>
          <label style={styles.label}>Background Color</label>
          <input
            type="color"
            value={bgColor}
            onChange={(e) => onBgColorChange(e.target.value)}
            style={styles.colorInput}
          />
        </div>
      </div>

      <div style={styles.logoUpload}>
        <input
          type="file"
          id="logo-upload"
          accept="image/*"
          onChange={handleLogoUpload}
          style={{ display: 'none' }}
        />
        <label htmlFor="logo-upload" style={styles.logoUploadContent}>
          {!logoPreview ? (
            <>
              <Upload size={24} style={styles.uploadIcon} />
              <div style={styles.uploadText}>
                <strong>Click to upload logo</strong>
                <br />
                PNG, JPG up to 1MB
              </div>
            </>
          ) : (
            <div style={styles.logoPreviewContainer}>
              <div style={styles.logoPreview}>
                <img src={logoPreview} alt="Logo preview" style={styles.logoImage} />
                <button onClick={handleRemoveLogo} style={styles.removeButton}>
                  <X size={16} />
                </button>
              </div>
              <div style={styles.logoControls}>
                <div style={styles.logoSizeControl}>
                  <label style={styles.label}>Logo Size: {logoSize}%</label>
                  <input
                    type="range"
                    min="10"
                    max="50"
                    value={logoSize}
                    onChange={handleLogoSizeChange}
                    style={styles.rangeInput}
                  />
                </div>
              </div>
            </div>
          )}
        </label>
      </div>
    </div>
  );
};

export default QRCodeCustomizer; 